import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { ChangeSoldToActionTypes } from '../../../../features/sold-to-base/store/actions/sold-to-change.action';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { ArticleStockInfo, StockLookup } from '../../../model';
import { bufferDebounceTime, normalizeHttpError, withdrawOn } from '../../../util';
import { StockInfoConnector } from '../../connectors/stock-info.connector';
import { UserActions } from '../actions';

@Injectable()
export class StockInfoEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout, ChangeSoldToActionTypes.ChangeSoldToSuccess));

  loadStockInfoDetails$: Observable<UserActions.LoadStockInfoDetailsSuccess | UserActions.LoadStockInfoDetailsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.StockInfoActionTypes.LoadStockInfoDetails),
        map((action: UserActions.LoadStockInfoDetails) => action.lookup),
        mergeMap((lookup: StockLookup) =>
          this.connector.getStockInfoDetails(lookup).pipe(
            map((stockInfo) => new UserActions.LoadStockInfoDetailsSuccess(stockInfo, lookup)),
            catchError((error) => of(new UserActions.LoadStockInfoDetailsFail(lookup, normalizeHttpError(error))))
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  loadStockInfo$: Observable<UserActions.LoadStockInfoSuccess | UserActions.LoadStockInfoFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.StockInfoActionTypes.LoadStockInfo),
      map((action: UserActions.LoadStockInfo) => action.lookup),
      bufferDebounceTime(30),
      mergeMap((stockLookups: StockLookup[]) =>
        this.connector.getStockInfo(stockLookups).pipe(
          map((list) => list.values),
          concatMap((responseValues: ArticleStockInfo[]) => {
            return stockLookups.map((lookup) => {
              const responseValue = responseValues.find(
                (rv) => rv.articleRef === lookup.articleNumber && rv.quantity === lookup.quantity && rv.unitRef === lookup.unit
              );
              if (responseValue) {
                return new UserActions.LoadStockInfoSuccess(responseValue);
              } else {
                return new UserActions.LoadStockInfoFail(lookup, undefined);
              }
            });
          }),
          catchError((error) => stockLookups.map((input) => new UserActions.LoadStockInfoFail(input, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(private actions$: Actions, private connector: StockInfoConnector) {}
}
